import { defineStore } from "pinia";
import appApi from "@/services/api/global/app";

// Estado inicial definido fuera del store
const getDefaultState = () => ({
  formStatusUnSaved: false,
  mainMenu: null,
  countries: null,
  timeZones: null,
  businessTypes: null,
  isLoading: false,
  showPatientTerm: 1, // Según el valor mostraríamos -> 0: Cliente 1: Paciente 
  showTreatmentTerm: 1, // Según el valor mostraríamos ->  0: Tratamientos 1: Servicios
  assistantId: localStorage.getItem("assistantId") || null, // Recuperamos assistantId desde localStorage si existe
  layoutMode: 'topbar', // Modo de layout: 'sidebar' para barra lateral, 'topbar' para barra superior
  // Promesas en vuelo para ensure* (no se persiste, se usan para deduplicar
  // llamadas concurrentes al mismo endpoint).
  _countriesInflight: null,
  _timeZonesInflight: null,
  _businessTypesInflight: null,

});

export const useLayoutStore = defineStore("layout", {
  state: getDefaultState,
  persist: false,
  actions: {

    setMainMenu(menu) {
      this.mainMenu = menu;
    },

    async refreshMainMenu() {
      try {
        console.log('🔄 Refrescando menú...');
        // Primero limpiar el menú actual para forzar re-render
        this.mainMenu = null;

        // Hacer la petición con cache busting
        const timestamp = new Date().getTime();
        const response = await appApi.getLayout(timestamp);

        // getLayout ahora devuelve `{menu, shell_settings}`. Para retro
        // compatibilidad, aceptamos tanto el nuevo formato como el array
        // antiguo por si algun entorno aun no esta actualizado.
        const menu = Array.isArray(response) ? response : response?.menu;

        this.mainMenu = menu;

        console.log('✅ Menú actualizado:', (menu?.length ?? 0), 'items');
      } catch (error) {
        console.error('❌ Error refreshing main menu:', error);
      }
    },

    setCoutries(countries){
      this.countries = countries
    },

    setTimeZones(timeZones){
      this.timeZones = timeZones
    },

    setBusinessTypes(businessTypes){
      this.businessTypes = businessTypes
    },

    // ----------------------------------------------------------------------
    // Lazy loaders: antes countries/businessTypes/timeZones se pedian en el
    // arranque del AuthenticatedLayout aunque muchisimas veces el usuario no
    // iba a entrar en pantallas que los necesitaran (customers, centers,
    // companies, entities, charges). Ahora solo se piden la primera vez que
    // alguien los consume. Las promesas en vuelo se memoizan (`_inflight`)
    // para evitar dispararlas dos veces si dos componentes las piden a la vez.
    // ----------------------------------------------------------------------
    async ensureCountries() {
      if (this.countries?.length) return this.countries;
      if (this._countriesInflight) return this._countriesInflight;
      this._countriesInflight = appApi.getCountries()
        .then(res => {
          this.setCoutries(res.data);
          return this.countries;
        })
        .catch(err => {
          console.error('Error cargando countries (lazy):', err);
          return [];
        })
        .finally(() => { this._countriesInflight = null; });
      return this._countriesInflight;
    },

    async ensureTimeZones() {
      if (this.timeZones?.length) return this.timeZones;
      if (this._timeZonesInflight) return this._timeZonesInflight;
      this._timeZonesInflight = appApi.getTimeZones()
        .then(res => {
          this.setTimeZones(res.data);
          return this.timeZones;
        })
        .catch(err => {
          console.error('Error cargando timeZones (lazy):', err);
          return [];
        })
        .finally(() => { this._timeZonesInflight = null; });
      return this._timeZonesInflight;
    },

    async ensureBusinessTypes() {
      if (this.businessTypes?.length) return this.businessTypes;
      if (this._businessTypesInflight) return this._businessTypesInflight;
      this._businessTypesInflight = appApi.getBusinessTypes()
        .then(res => {
          this.setBusinessTypes(res);
          return this.businessTypes;
        })
        .catch(err => {
          console.error('Error cargando businessTypes (lazy):', err);
          return [];
        })
        .finally(() => { this._businessTypesInflight = null; });
      return this._businessTypesInflight;
    },

     setLayoutMode(layoutMode){
      this.layoutMode = layoutMode
    },

    setFormStatus(status) {
      this.formStatusUnSaved = status;
    },
    setPatientTreatmentTerm(showPatientTerm, showTreatmentTerm){
      this.showPatientTerm = showPatientTerm;
      this.showTreatmentTerm = showTreatmentTerm;
    },

    setLoading(loading) {
      this.isLoading = loading;
    },

    setLogout(){
      // Restablece el estado a su estado inicial
      Object.assign(this, getDefaultState());
    }

  },
});
